import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminSignIn, AdminNav } from "@/components/admin/AdminShell";
import { toCsv, downloadCsv } from "@/lib/csv-export";
import { getSalesReport, type SalesRange, type SalesReport } from "@/lib/sales-stats.functions";

export const Route = createFileRoute("/admin/sales")({
  component: SalesDashboard,
  head: () => ({
    meta: [
      { title: "Sales & Revenue — SFN (internal)" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Internal SFN token sales and revenue analytics." },
    ],
  }),
});

const RANGES: { key: SalesRange; label: string }[] = [
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All time" },
];

const num = (v: number, d = 2) =>
  v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl glass p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-medium tracking-tight">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function SalesDashboard() {
  const fetchReport = useServerFn(getSalesReport);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [range, setRange] = useState<SalesRange>("24h");
  const [report, setReport] = useState<SalesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSignedIn(Boolean(data.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(Boolean(session));
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const load = useCallback(() => {
    if (signedIn !== true) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchReport({ data: { range } })
      .then((r) => {
        setReport(r as SalesReport);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load sales analytics.");
        setLoading(false);
      });
  }, [fetchReport, range, signedIn]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    if (!report || report.rows.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    const csv = toCsv([
      ["id", "created_at", "token", "pay_amount", "pay_currency", "receive_amount", "usd_value", "wallet", "signature", "cluster"],
      ...report.rows.map((r) => [
        r.id,
        r.created_at,
        r.token,
        r.pay_amount,
        r.pay_currency,
        r.receive_amount,
        r.usd_value,
        r.wallet ?? "",
        r.signature,
        r.cluster,
      ]),
    ]);
    downloadCsv(`sfn-sales-${report.range}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    toast.success(`Exported ${report.rows.length} sales.`);
  };

  if (signedIn === false) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h1 className="font-display text-3xl font-medium tracking-tight">Sales &amp; revenue</h1>
        <AdminSignIn note="Internal sales ledger — only readable by an authenticated account." />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h1 className="font-display text-3xl font-medium tracking-tight">Sales &amp; revenue analytics</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Every completed MEAL2 / MEAL4 settlement, synced from the purchase flow.
      </p>
      <AdminNav current="sales" />

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-full glass p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                range === r.key
                  ? "bg-white/[0.12] text-foreground"
                  : "text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label="Download the sales report as a CSV file"
          onClick={exportCsv}
          className="rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-foreground hover:bg-white/[0.06]"
        >
          Export sales report (CSV)
        </button>
        <button
          type="button"
          onClick={load}
          className="rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-white/[0.06]"
        >
          Refresh
        </button>
      </div>

      {loading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="mt-8 text-sm text-coral">{error}</p>}

      {report && !loading && (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Today (24h) revenue"
              value={`$${num(report.today.usd)}`}
              sub={`${report.today.orders} orders · ${report.today.uniqueWallets} wallets`}
            />
            <Stat
              label="Lifetime revenue"
              value={`$${num(report.lifetime.usd)}`}
              sub={`${report.lifetime.orders} orders total`}
            />
            <Stat
              label={`MEAL4 sold (${report.range})`}
              value={num(report.window.meal4Tokens)}
              sub={`MEAL2: ${num(report.window.meal2Tokens)}`}
            />
            <Stat
              label="Treasury balance"
              value={report.treasurySol === null ? "—" : `${num(report.treasurySol, 4)} SOL`}
              sub={report.treasurySol === null ? "RPC unavailable" : "Live on-chain read"}
            />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Stat label={`SOL in (${report.range})`} value={num(report.window.sol, 4)} />
            <Stat label={`USDC in (${report.range})`} value={num(report.window.usdc)} />
            <Stat label={`Unique buyers (${report.range})`} value={String(report.window.uniqueWallets)} />
          </div>

          <div className="mt-8 overflow-x-auto rounded-2xl glass">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Token</th>
                  <th className="px-4 py-3">Paid</th>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3">USD</th>
                  <th className="px-4 py-3">Wallet</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-muted-foreground">
                      No sales recorded in this period yet.
                    </td>
                  </tr>
                )}
                {report.rows.map((r) => (
                  <tr key={r.id} className="border-t border-white/[0.06]">
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-semibold">{r.token}</td>
                    <td className="px-4 py-3">
                      {num(Number(r.pay_amount), 4)} {r.pay_currency}
                    </td>
                    <td className="px-4 py-3">{num(Number(r.receive_amount))}</td>
                    <td className="px-4 py-3">${num(Number(r.usd_value))}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {r.wallet ? `${r.wallet.slice(0, 4)}…${r.wallet.slice(-4)}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}
